/**
 * Errors described for the person on the screen (CLAUDE.md §6). The server
 * writes most messages itself; these are the cases where the same server
 * answer needs different words depending on which screen asked, or must
 * point at one field of a form. Also the words for the choices on the admin
 * Users page, which say why a physician cannot be chosen.
 */
import { compareDates } from '../lib/dates/iso.ts';
import { formatDay, formatPeriod, formatRange } from '../lib/format/dates.ts';
import { ApiFailure, type Physician, type User, type WeekChange } from './api.ts';
import { copy } from './copy/en-CA.ts';

/**
 * A failed password change. At first sign-in the only password a person has
 * is the temporary one, so a wrong one is called that, not "current". The
 * first-sign-in page no longer asks for it; the server lets it be left out for
 * 15 minutes after signing in, and after that asks the person to sign in again.
 */
export function describePasswordFailure(err: unknown, mode: 'temporary' | 'own'): string {
  if (!(err instanceof ApiFailure)) return copy.errors.unexpected;
  if (err.code === 'currentPasswordWrong') return mode === 'temporary' ? copy.changePassword.wrongTemporary : copy.changePassword.own.wrong;
  if (err.code === 'signInAgain') return copy.changePassword.signInAgain;
  return err.message;
}

/**
 * A failed change on the admin Users page. The server words these for the
 * admin, naming the account a physician already has when that is why; what
 * did not come from the server gets our own words, never a raw error.
 */
export function describeAccountFailure(err: unknown): string {
  return err instanceof ApiFailure ? err.message : copy.errors.unexpected;
}

export interface PhysicianChoice {
  id: string;
  label: string;
  disabled: boolean;
}

/**
 * The physicians an account can be linked to, in roster order. `account` is
 * the account whose dropdown this is, or null for a new account.
 *
 * One account per physician (owner, 24 October 2026): a physician linked to
 * another account is listed, saying so, but cannot be chosen. The account's
 * own physician is always listed and choosable, even when deactivated (marked
 * so) and even when an older account shares them, so a physician account's
 * dropdown never shows "Not a physician". Other deactivated physicians are
 * left out, as before.
 */
export function describePhysicianChoices(
  physicians: readonly Physician[],
  accounts: readonly Pick<User, 'id' | 'physicianId'>[],
  account: Pick<User, 'id' | 'physicianId'> | null,
): PhysicianChoice[] {
  const own = account?.physicianId ?? null;
  const taken = new Set(accounts.filter((u) => u.id !== account?.id && u.physicianId !== null).map((u) => u.physicianId));
  return physicians
    .filter((p) => p.active || p.id === own)
    .map((p) => {
      if (p.id === own) return { id: p.id, label: p.active ? p.name : copy.admin.users.deactivatedPhysician(p.name), disabled: false };
      if (taken.has(p.id)) return { id: p.id, label: copy.admin.users.hasAccount(p.name), disabled: true };
      return { id: p.id, label: p.name, disabled: false };
    });
}

export type PhysicianProblem = keyof typeof copy.admin.physicians.problems;
export type PhysicianField = 'name' | 'initials' | 'endDate';

const PROBLEM_FIELD: Record<PhysicianProblem, PhysicianField> = { nameRequired: 'name', initialsRequired: 'initials', endBeforeStart: 'endDate' };

/** The server's codes for the same problems the form checks. */
const SERVER_PROBLEM = new Map<string, PhysicianProblem>([
  ['physicianNameRequired', 'nameRequired'],
  ['physicianInitialsRequired', 'initialsRequired'],
  ['physicianEndBeforeStart', 'endBeforeStart'],
]);

/**
 * What the physician form checks before sending; the server checks the same
 * again. Dates are ISO strings, '' when blank. `initials` and `endDate` are
 * left out by the add form, which has neither. The deactivate-on date is
 * the last day on the rotation, so the same day as the start is allowed.
 */
export function checkPhysician(form: { name: string; initials?: string; startDate: string; endDate?: string }): PhysicianProblem | null {
  if (form.name.trim() === '') return 'nameRequired';
  if (form.initials?.trim() === '') return 'initialsRequired';
  if (form.startDate !== '' && form.endDate && compareDates(form.endDate, form.startDate) < 0) return 'endBeforeStart';
  return null;
}

/** A problem in the physician form, with the field it is about, so the page can mark that field and move to it. */
export function describePhysicianProblem(problem: PhysicianProblem): { message: string; field: PhysicianField } {
  return { message: copy.admin.physicians.problems[problem], field: PROBLEM_FIELD[problem] };
}

/**
 * A failed save of a physician. The server's refusals of a blank name, blank
 * initials or dates out of order point at their field, in the same words as
 * the form's own check; anything else is the server's message, or ours.
 */
export function describePhysicianFailure(err: unknown): { message: string; field: PhysicianField | null } {
  const problem = err instanceof ApiFailure ? SERVER_PROBLEM.get(err.code) : undefined;
  if (problem) return describePhysicianProblem(problem);
  return { message: err instanceof ApiFailure ? err.message : copy.errors.unexpected, field: null };
}

/**
 * A failed action on the admin's Availability page: opening, closing,
 * reopening or deleting a request period, deciding a request, or changing
 * a week on the grid. A new period refused for overlapping another names
 * that period and its dates, day first; anything else is the server's own
 * words, or ours when the server could not be reached.
 */
export function describeAvailabilityFailure(err: unknown): string {
  if (!(err instanceof ApiFailure)) return copy.errors.unexpected;
  if (err.code === 'requestPeriodOverlaps' && err.period) {
    return copy.admin.availability.overlaps(err.period.name, formatPeriod(err.period.startDate, err.period.endDate));
  }
  return err.message;
}

/**
 * One line of "Your on-call weeks changed", in three parts: the week's true
 * on-call range ("Mon 8 Feb – Mon 15 Feb" for a week over a holiday
 * Monday), what changed for the physician reading it, and who changed it
 * and on what day.
 */
export function describeWeekChange(change: WeekChange): { range: string; what: string; by: string } {
  const c = copy.weekChanges;
  const what = change.gained ? (change.from ? c.gained(change.from.name) : c.gainedFromNobody) : change.to ? c.lost(change.to.name) : c.lostToNobody;
  const day = formatDay(change.changedOn);
  return { range: formatRange(change.onCallStart, change.onCallEnd), what, by: change.changedBy ? c.by(change.changedBy, day) : c.byUnknown(day) };
}

/**
 * A failure on that notice: loading it, or "Got it". The notice sits beside
 * a schedule that did load, so a failed load says the schedule shown is up
 * to date; a failed "Got it" says the notice would come back. Never the raw
 * error: the person can only reload or press again either way.
 */
export function describeWeekChangesFailure(_err: unknown, action: 'load' | 'seen'): string {
  return action === 'load' ? copy.weekChanges.loadFailed : copy.weekChanges.seenFailed;
}

/**
 * The To do page could not load its list. Said as such, never as "Nothing
 * waiting", which would hide an outage behind a calm empty page; with the
 * server's own words, or ours when it could not be reached.
 */
export function describeTodoFailure(err: unknown): string {
  return `${copy.admin.todo.unavailable} ${err instanceof ApiFailure ? err.message : copy.errors.unexpected}`;
}
